/**
 * LocatoreFields — F-003.2 Phase 2 (IMPL-P2-001) + F-005 Phase 5 (IMPL-P5-004).
 *
 * Field group per il locatore (step 0 del wizard). Riusa CFInput da features/profilo
 * tramite Controller wrapper (CFInput è controlled — non RHF-aware nativamente).
 * Tutti i campi si collegano al FormProvider parent via useFormContext.
 *
 * F-005: i campi contestati (presenti in `useErrorFields()`) ricevono un
 * ring amber + un `<span>` sr-only descrittivo collegato via aria-describedby
 * (vedi `contestedFieldUtils`).
 *
 * Props:
 *  - showTooltips: true nel wizard (F-003.2), false nel form denso (F-003.3)
 */
import { Controller, useFormContext } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CFInput } from '@/features/profilo/components/CFInput';
import { cn } from '@/lib/utils';
import { useErrorFields } from '../../hooks/useErrorFields';
import {
  CONTESTED_HINT_TEXT,
  CONTESTED_RING_CLASS,
  contestedHintId,
} from './contestedFieldUtils';

export interface LocatoreFieldsProps {
  showTooltips?: boolean;
}

export function LocatoreFields({ showTooltips: _showTooltips = false }: LocatoreFieldsProps) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ContractDraft>();

  const loc = errors.locatore;
  const errorFields = useErrorFields();

  const cfPath = 'locatore.codiceFiscale';
  const nomePath = 'locatore.nome';
  const cognomePath = 'locatore.cognome';
  const isCfContested = errorFields.includes(cfPath);
  const isNomeContested = errorFields.includes(nomePath);
  const isCognomeContested = errorFields.includes(cognomePath);

  return (
    <div className="space-y-4">
      {/* Codice Fiscale — usa CFInput via Controller */}
      <div>
        <Label htmlFor="locatore-cf">Codice fiscale *</Label>
        <Controller
          name="locatore.codiceFiscale"
          control={control}
          render={({ field, fieldState }) => (
            <CFInput
              id="locatore-cf"
              value={field.value ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
              error={fieldState.error?.message}
              cfType="PF"
              className={cn(isCfContested && CONTESTED_RING_CLASS)}
              aria-describedby={isCfContested ? contestedHintId(cfPath) : undefined}
            />
          )}
        />
        {isCfContested && (
          <span id={contestedHintId(cfPath)} className="sr-only">
            {CONTESTED_HINT_TEXT}
          </span>
        )}
      </div>

      {/* Nome + Cognome in griglia 2 colonne */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Nome */}
        <div className="space-y-1">
          <Label htmlFor="locatore-nome">Nome *</Label>
          <Controller
            name="locatore.nome"
            control={control}
            render={({ field }) => (
              <Input
                id="locatore-nome"
                {...field}
                value={field.value ?? ''}
                aria-invalid={!!loc?.nome}
                aria-describedby={
                  loc?.nome
                    ? 'locatore-nome-error'
                    : isNomeContested
                      ? contestedHintId(nomePath)
                      : undefined
                }
                className={cn(isNomeContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {loc?.nome && (
            <p id="locatore-nome-error" className="text-xs text-destructive">
              {loc.nome.message}
            </p>
          )}
          {isNomeContested && (
            <span id={contestedHintId(nomePath)} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>

        {/* Cognome */}
        <div className="space-y-1">
          <Label htmlFor="locatore-cognome">Cognome</Label>
          <Controller
            name="locatore.cognome"
            control={control}
            render={({ field }) => (
              <Input
                id="locatore-cognome"
                {...field}
                value={field.value ?? ''}
                aria-invalid={!!loc?.cognome}
                aria-describedby={
                  loc?.cognome
                    ? 'locatore-cognome-error'
                    : isCognomeContested
                      ? contestedHintId(cognomePath)
                      : undefined
                }
                className={cn(isCognomeContested && CONTESTED_RING_CLASS)}
              />
            )}
          />
          {loc?.cognome && (
            <p id="locatore-cognome-error" className="text-xs text-destructive">
              {loc.cognome.message}
            </p>
          )}
          {isCognomeContested && (
            <span id={contestedHintId(cognomePath)} className="sr-only">
              {CONTESTED_HINT_TEXT}
            </span>
          )}
        </div>
      </div>

      {/* Indirizzo — 5 campi in griglia */}
      <div>
        <p className="text-sm font-medium mb-2">Indirizzo</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Via */}
          <div className="space-y-1">
            <Label htmlFor="locatore-via">Via / Piazza *</Label>
            <Controller
              name="locatore.indirizzo.via"
              control={control}
              render={({ field }) => (
                <Input
                  id="locatore-via"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!loc?.indirizzo?.via}
                  aria-describedby={loc?.indirizzo?.via ? 'locatore-via-error' : undefined}
                />
              )}
            />
            {loc?.indirizzo?.via && (
              <p id="locatore-via-error" className="text-xs text-destructive">
                {loc.indirizzo.via.message}
              </p>
            )}
          </div>

          {/* Civico */}
          <div className="space-y-1">
            <Label htmlFor="locatore-civico">Civico *</Label>
            <Controller
              name="locatore.indirizzo.civico"
              control={control}
              render={({ field }) => (
                <Input
                  id="locatore-civico"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!loc?.indirizzo?.civico}
                  aria-describedby={loc?.indirizzo?.civico ? 'locatore-civico-error' : undefined}
                />
              )}
            />
            {loc?.indirizzo?.civico && (
              <p id="locatore-civico-error" className="text-xs text-destructive">
                {loc.indirizzo.civico.message}
              </p>
            )}
          </div>

          {/* CAP */}
          <div className="space-y-1">
            <Label htmlFor="locatore-cap">CAP *</Label>
            <Controller
              name="locatore.indirizzo.cap"
              control={control}
              render={({ field }) => (
                <Input
                  id="locatore-cap"
                  maxLength={5}
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!loc?.indirizzo?.cap}
                  aria-describedby={loc?.indirizzo?.cap ? 'locatore-cap-error' : undefined}
                />
              )}
            />
            {loc?.indirizzo?.cap && (
              <p id="locatore-cap-error" className="text-xs text-destructive">
                {loc.indirizzo.cap.message}
              </p>
            )}
          </div>

          {/* Comune */}
          <div className="space-y-1">
            <Label htmlFor="locatore-comune">Comune *</Label>
            <Controller
              name="locatore.indirizzo.comune"
              control={control}
              render={({ field }) => (
                <Input
                  id="locatore-comune"
                  {...field}
                  value={field.value ?? ''}
                  aria-invalid={!!loc?.indirizzo?.comune}
                  aria-describedby={loc?.indirizzo?.comune ? 'locatore-comune-error' : undefined}
                />
              )}
            />
            {loc?.indirizzo?.comune && (
              <p id="locatore-comune-error" className="text-xs text-destructive">
                {loc.indirizzo.comune.message}
              </p>
            )}
          </div>

          {/* Provincia */}
          <div className="space-y-1">
            <Label htmlFor="locatore-provincia">Provincia *</Label>
            <Controller
              name="locatore.indirizzo.provincia"
              control={control}
              render={({ field }) => (
                <Input
                  id="locatore-provincia"
                  maxLength={2}
                  className="uppercase"
                  {...field}
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                  aria-invalid={!!loc?.indirizzo?.provincia}
                  aria-describedby={
                    loc?.indirizzo?.provincia ? 'locatore-provincia-error' : undefined
                  }
                />
              )}
            />
            {loc?.indirizzo?.provincia && (
              <p id="locatore-provincia-error" className="text-xs text-destructive">
                {loc.indirizzo.provincia.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
